import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
// to avoid circular dependency
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { FACEBOOK_CONFIG } from '@freelancer/facebook/facebook.config';
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { FacebookConfig } from '@freelancer/facebook/facebook.interface';
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { GOOGLE_SIGN_IN_CONFIG } from '@freelancer/google-sign-in/google-sign-in.config';
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { GoogleSignInConfig } from '@freelancer/google-sign-in/google-sign-in.interface';

/**
 * Thin wrapper around the `@capgo/capacitor-social-login` (`SocialLogin`)
 * native plugin.
 */
@Injectable({
  providedIn: 'root',
})
export class SocialLoginPlugin {
  private googleConfig = inject<GoogleSignInConfig>(GOOGLE_SIGN_IN_CONFIG);
  private facebookConfig = inject<FacebookConfig>(FACEBOOK_CONFIG);
  private errorHandler = inject(ErrorHandler);

  // `SocialLogin.initialize()` may only be called ONCE per app process
  // https://capgo.app/docs/plugins/social-login/google/web/
  private initPromise?: Promise<void>;

  isSupported(): boolean {
    return Capacitor.isPluginAvailable('SocialLogin');
  }

  initialize(): Promise<void> {
    if (this.initPromise === undefined) {
      this.initPromise = this.doInitialize();
    }
    return this.initPromise;
  }

  private async doInitialize(): Promise<void> {
    if (!this.isSupported()) {
      return;
    }
    const google =
      this.googleConfig.clientId !== undefined
        ? {
            webClientId: this.googleConfig.clientId,
            ...(this.googleConfig.iosClientId !== undefined
              ? { iOSClientId: this.googleConfig.iosClientId }
              : {}),
            mode: 'online' as const,
          }
        : undefined;
    const facebook =
      this.facebookConfig.appId !== undefined &&
      this.facebookConfig.clientToken !== undefined
        ? {
            appId: this.facebookConfig.appId,
            clientToken: this.facebookConfig.clientToken,
          }
        : undefined;
    try {
      await SocialLogin.initialize({
        ...(google !== undefined ? { google } : {}),
        ...(facebook !== undefined ? { facebook } : {}),
      });
    } catch (err) {
      this.errorHandler.handleError(err);
    }
  }
}
